/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { Grid, PageGridContainer, Typography2, breakpoints } from '@coursera/cds-core';

import ConduitSvg from 'bundles/partners-graphql/components/listing/cds/ConduitSvg';
import HeroPartnerLogos from 'bundles/partners-graphql/components/listing/cds/HeroPartnerLogos';
import { HIDDEN_PARTNER_COUNTRY_CODES } from 'bundles/partners-graphql/constants';
import type { PartnerListingPagePartnerFragment as PartnerListingPagePartner } from 'bundles/partners-graphql/queries/__generated__/GetAllPartners';

import _t from 'i18n!nls/partners-graphql';

type Props = {
  partners: PartnerListingPagePartner[];
};

const styles = {
  hero: css`
    background: linear-gradient(
      to right,
      var(--cds-color-emphasis-primary-background-xweak),
      var(--cds-color-neutral-background-primary)
    );
    border-bottom: 1px solid var(--cds-color-neutral-stroke-primary-weak);
    overflow: hidden;
  `,
  row: css`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    min-height: 204px;

    ${breakpoints.down('xs')} {
      height: 156px;
      min-height: 156px;
    }
  `,
  content: css`
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-50);
    max-width: 400px;
    padding: var(--cds-spacing-400) 0;
  `,
  rightSide: css`
    position: relative;
    flex-shrink: 0;
    width: 60%;
    align-self: stretch;

    ${breakpoints.down('xs')} {
      width: 40%;
    }
  `,
  conduit: css`
    position: absolute;
    top: 100px;
    left: calc(50% + 50px);
    transform: translate(-50%, -50%);
    width: 909px;
    height: auto;

    ${breakpoints.down('sm')} {
      width: 749px;
    }

    ${breakpoints.down('xs')} {
      top: 240px;
      right: -996px;
      left: unset;
    }
  `,
};

const PartnerListingHero = ({ partners }: Props) => {
  const partnerCount = partners.length;
  const uniqueCountries = new Set(
    partners
      .map((p) => p?.location?.country?.toUpperCase())
      .filter((code): code is string => !!code && !HIDDEN_PARTNER_COUNTRY_CODES.includes(code))
  );
  const countryCount = uniqueCountries.size;

  return (
    <div css={styles.hero} data-testid="partner-listing-hero">
      <PageGridContainer>
        <Grid item xs={12}>
          <div css={styles.row}>
            <div css={styles.content}>
              <Typography2 component="h1" variant="titleLargeCompact">
                {_t('Meet our partners')}
              </Typography2>
              <Typography2 component="p" variant="bodyPrimary" color="supportText">
                {_t('#{partnerCount} partners across #{countryCount} countries', {
                  partnerCount,
                  countryCount,
                })}
              </Typography2>
            </div>

            <div css={styles.rightSide}>
              <ConduitSvg css={styles.conduit} />
              <HeroPartnerLogos partners={partners} />
            </div>
          </div>
        </Grid>
      </PageGridContainer>
    </div>
  );
};

export default PartnerListingHero;
